// workflowStepAttachmentUtils.js
// Helpers for related-node attachments on workflow step TASK records.

import { isRelatedTypeAllowed } from 'components/addmenu/addMenuTypeFilters';
import { buildRichTextSummary } from 'utilities/RichText';
import { formatNodeTemporalDisplay } from 'node/form/fields/utilities/nodeTemporalDisplay';

const NOTE_CONTENTS_SNIPPET_MAX = 100;
const EVENT_WHEN_FIELD_NAMES = Object.freeze(['when', 'dateTimeRange', 'datetimeRange']);

/** Root field on a TASK child created from a checklist-item action. */
export const WORKFLOW_CHECKLIST_ITEM_KEY_FIELD = 'workflowChecklistItemKey';

/**
 * @param {object|null|undefined} node
 * @returns {string}
 */
export function getWorkflowChecklistItemKey(node) {
  return String(node?.[WORKFLOW_CHECKLIST_ITEM_KEY_FIELD] || '').trim();
}

/**
 * @param {unknown} itemKey
 * @returns {{ workflowChecklistItemKey: string }|null}
 */
export function buildWorkflowChecklistItemChildFields(itemKey) {
  const key = String(itemKey || '').trim();
  return key ? { [WORKFLOW_CHECKLIST_ITEM_KEY_FIELD]: key } : null;
}

/**
 * Stamps a checklist item key onto a child node about to be created.
 *
 * @param {object|null|undefined} target
 * @param {unknown} fieldsOrKey - Item key, or an object with the stamp field.
 */
export function applyWorkflowChecklistItemKey(target, fieldsOrKey) {
  if (!target || typeof target !== 'object') return;
  const key = typeof fieldsOrKey === 'string'
    ? String(fieldsOrKey || '').trim()
    : getWorkflowChecklistItemKey(fieldsOrKey);
  if (!key) return;
  target[WORKFLOW_CHECKLIST_ITEM_KEY_FIELD] = key;
}

/**
 * @param {Array<object>|null|undefined} nodes
 * @param {unknown} itemKey
 * @returns {Array<object>}
 */
export function filterWorkflowStepAttachmentsForItem(nodes, itemKey) {
  const key = String(itemKey || '').trim();
  if (!key) return [];
  return (Array.isArray(nodes) ? nodes : []).filter((node) => (
    getWorkflowChecklistItemKey(node) === key
  ));
}

/**
 * Returns whether a related node is eligible to show as a step attachment.
 * When relatedTypeKeys is set (host suggestedChildren), only those child types pass.
 *
 * @param {object|null|undefined} node
 * @param {string[]|null|undefined} [relatedTypeKeys] - Host type suggestedChildren allowlist.
 * @returns {boolean}
 */
export function isWorkflowStepAttachmentNode(node, relatedTypeKeys = null) {
  if (!String(node?.key || '').trim()) return false;
  return isRelatedTypeAllowed(relatedTypeKeys, node?.type);
}

/**
 * Filters related neighbors to attachment rows allowed by Related Types, newest first.
 *
 * @param {Array<object>|null|undefined} nodes
 * @param {string[]|null|undefined} [relatedTypeKeys] - Host type suggestedChildren allowlist.
 * @returns {Array<object>}
 */
export function filterWorkflowStepAttachmentNodes(nodes, relatedTypeKeys = null) {
  const rows = (Array.isArray(nodes) ? nodes : [])
    .filter((node) => isWorkflowStepAttachmentNode(node, relatedTypeKeys));

  return rows.sort((left, right) => {
    const leftMs = Number(left?.updatedAtMs || left?.createdAtMs || 0);
    const rightMs = Number(right?.updatedAtMs || right?.createdAtMs || 0);
    return rightMs - leftMs;
  });
}

/**
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
export function isWorkflowStepAttachmentPhotoNode(node) {
  return String(node?.type || '').trim().toUpperCase() === 'PHOTO';
}

/**
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
export function isWorkflowStepAttachmentSketchNode(node) {
  return String(node?.type || '').trim().toUpperCase() === 'SKETCH';
}

/**
 * Splits attachments into PHOTO and SKETCH thumbnail rows, plus all other rows.
 *
 * @param {Array<object>|null|undefined} nodes
 * @returns {{ photos: object[], sketches: object[], others: object[] }}
 */
export function partitionWorkflowStepAttachments(nodes) {
  const photos = [];
  const sketches = [];
  const others = [];

  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    if (!node || typeof node !== 'object') return;
    if (isWorkflowStepAttachmentPhotoNode(node)) {
      photos.push(node);
      return;
    }
    if (isWorkflowStepAttachmentSketchNode(node)) {
      sketches.push(node);
      return;
    }
    others.push(node);
  });

  return { photos, sketches, others };
}

/**
 * Display label for a related attachment node.
 *
 * @param {object|null|undefined} node
 * @returns {string}
 */
export function getWorkflowStepAttachmentLabel(node) {
  const title = String(node?.title || node?.name || '').trim();
  if (title) return title;

  const fileName = String(node?.fileName || '').trim();
  if (fileName) return fileName;

  const typeKey = String(node?.type || '').trim().toUpperCase();
  if (typeKey) {
    return typeKey.charAt(0) + typeKey.slice(1).toLowerCase();
  }

  return 'Attachment';
}

/**
 * Locates rich-text `contents` on a NOTE (root or data_* bag).
 *
 * @param {object|null|undefined} node
 * @returns {*|null}
 */
export function resolveWorkflowStepNoteContentsValue(node) {
  if (!node || typeof node !== 'object') return null;

  if (node.contents != null) return node.contents;

  const typeKey = String(node.type || '').trim().toLowerCase();
  if (typeKey) {
    const typedBag = node[`data_${typeKey}`];
    if (typedBag && typeof typedBag === 'object' && typedBag.contents != null) {
      return typedBag.contents;
    }
  }

  const bagKeys = Object.keys(node).filter((key) => key.startsWith('data_'));
  for (let index = 0; index < bagKeys.length; index += 1) {
    const bag = node[bagKeys[index]];
    if (bag && typeof bag === 'object' && bag.contents != null) {
      return bag.contents;
    }
  }

  return null;
}

/**
 * Plain-text preview of a NOTE's contents for attachment rows.
 *
 * @param {object|null|undefined} node
 * @param {number} [maxLength=100]
 * @returns {string}
 */
export function getWorkflowStepAttachmentNoteSnippet(
  node,
  maxLength = NOTE_CONTENTS_SNIPPET_MAX,
) {
  const typeKey = String(node?.type || '').trim().toUpperCase();
  if (typeKey !== 'NOTE') return '';

  const limit = Number.isFinite(Number(maxLength)) && Number(maxLength) > 0
    ? Math.floor(Number(maxLength))
    : NOTE_CONTENTS_SNIPPET_MAX;

  const contents = resolveWorkflowStepNoteContentsValue(node);
  if (contents == null) return '';

  // Prefer denormalized plain text on the rich-text value (fast + reliable).
  if (typeof contents === 'string') {
    return String(buildRichTextSummary(contents, limit) || '').trim();
  }
  if (typeof contents === 'object' && typeof contents.text === 'string') {
    const fromText = String(buildRichTextSummary(contents.text, limit) || '').trim();
    if (fromText) return fromText;
  }

  return String(buildRichTextSummary(contents, limit) || '').trim();
}

/**
 * Locates the EVENT `when` (datetimerange) value on a node.
 *
 * @param {object|null|undefined} node
 * @returns {*|null}
 */
export function resolveWorkflowStepEventWhenValue(node) {
  if (!node || typeof node !== 'object') return null;

  const readWhenFromBag = (bag) => {
    if (!bag || typeof bag !== 'object') return null;
    for (let index = 0; index < EVENT_WHEN_FIELD_NAMES.length; index += 1) {
      const fieldName = EVENT_WHEN_FIELD_NAMES[index];
      if (bag[fieldName] != null) return bag[fieldName];
    }
    return null;
  };

  for (let index = 0; index < EVENT_WHEN_FIELD_NAMES.length; index += 1) {
    const fieldName = EVENT_WHEN_FIELD_NAMES[index];
    if (node[fieldName] != null) return node[fieldName];
  }

  const typeKey = String(node.type || '').trim().toLowerCase();
  if (typeKey) {
    const fromTyped = readWhenFromBag(node[`data_${typeKey}`]);
    if (fromTyped != null) return fromTyped;
  }

  const bagKeys = Object.keys(node).filter((key) => key.startsWith('data_'));
  for (let index = 0; index < bagKeys.length; index += 1) {
    const fromBag = readWhenFromBag(node[bagKeys[index]]);
    if (fromBag != null) return fromBag;
  }

  return null;
}

/**
 * Formatted EVENT `when` value for attachment rows.
 *
 * @param {object|null|undefined} node
 * @returns {string}
 */
export function getWorkflowStepAttachmentEventWhenSnippet(node) {
  const typeKey = String(node?.type || '').trim().toUpperCase();
  if (typeKey !== 'EVENT') return '';

  const whenValue = resolveWorkflowStepEventWhenValue(node);
  if (whenValue == null) return '';

  return String(formatNodeTemporalDisplay(whenValue, 'datetimerange') || '').trim();
}

/**
 * Title + optional NOTE contents / EVENT when snippet for attachment list rows.
 *
 * @param {object|null|undefined} node
 * @returns {{ title: string, snippet: string }}
 */
export function getWorkflowStepAttachmentPresentation(node) {
  const title = getWorkflowStepAttachmentLabel(node);
  const typeKey = String(node?.type || '').trim().toUpperCase();

  const snippet = typeKey === 'EVENT'
    ? getWorkflowStepAttachmentEventWhenSnippet(node)
    : getWorkflowStepAttachmentNoteSnippet(node);

  if (!snippet) return { title, snippet: '' };

  // Avoid repeating the same text when title already matches the opening contents.
  const titleKey = title.toLowerCase();
  const snippetKey = snippet.toLowerCase();
  if (titleKey && (snippetKey === titleKey || snippetKey.startsWith(`${titleKey} `))) {
    return { title, snippet: '' };
  }

  return { title, snippet };
}
